import React, { useState, useEffect } from "react";

import CaseDialog from "./CaseDialog";
import Content from "./Content";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InfoSidebar from "./InfoSidebar";

import { CaseWithImages, ImageAndQuery, QueryDetails, QueryObjectType } from "../models";

import { hasOpenQueries, hasAnsweredQueries } from "../slices/queryDetails";

import {
  faArrowLeft,
  faArrowRight,
  faClipboard,
  faEye,
  faFlag
} from "@fortawesome/free-solid-svg-icons";
import SidebarPanelCase from "./SidebarPanelCase";
import styled from "styled-components";
import SidebarPanelAnnotations from "./SidebarPanelAnnotations";
import SidebarPanelQueries from "./SidebarPanelQueries";
import { useAppSelector } from "../hooks";
import { LoggedInUser } from "../permissions";

interface Props {
  readonly studyName: string | null;
  readonly histoCase: CaseWithImages;
  readonly selectedImage: ImageAndQuery | null;
  readonly userCanDeleteAnnotations: boolean;
  readonly queryDetails: QueryDetails;
  readonly loggedInUser: LoggedInUser;
  readonly hasAiAnnotations: boolean;
}

const PanelIcon = styled.div`
  height: 50px;
  width: 50px;
  padding: 15px;
  cursor: pointer;
  margin-top: 0px;
  margin-bottom: 4px;
  transition: opacity 0.2s ease-in-out;
`;

const NotificationDot = styled.div`
  position: absolute;
  left: -13px;
  top: -3px;
  width: 11px;
  height: 11px;
  background-color: red;
  border-radius: 50%;
  border: 2px solid white;
`;

const InfoSidebarCase = ({
  studyName,
  histoCase,
  selectedImage,
  userCanDeleteAnnotations,
  queryDetails,
  loggedInUser,
  hasAiAnnotations
}: Props) => {
  const user = useAppSelector(state => state.auth.user);

  const [hideSidebar, setHideSidebar] = useState(false);

  const toggleHideSidebar = function () {
    hideSidebar ? setHideSidebar(false) : setHideSidebar(true);
  };

  const [selectedPanel, setSelectedPanel] = useState(0);
  const [hoveredPanel, setHoveredPanel] = useState(-1);

  const selectPanel = function (index: number) {
    setSelectedPanel(index);
    setHideSidebar(false);
  };

  const isNotificationQueryIcon = function () {
    return (
      hasOpenQueries(queryDetails, histoCase, selectedImage) ||
      hasAnsweredQueries(queryDetails, histoCase, selectedImage)
    );
  };

  const [notificationQueryIcon, setNotificationQueryIcon] = useState(isNotificationQueryIcon());

  useEffect(() => {
    setNotificationQueryIcon(isNotificationQueryIcon());
  }, [queryDetails]);

  const panels = [
    {
      // Added transform as spacing between eye icon seems strange as it's height looks smaller than the rest
      icon: (
        <FontAwesomeIcon
          icon={faEye}
          title="Overview"
          style={{ width: "100%", height: "100%", marginBottom: "-2px", transform: "scale(1.1)" }}
        />
      ),
      content: (
        <SidebarPanelCase
          studyName={studyName}
          histoCase={histoCase}
          selectedImage={selectedImage}
          loggedInUser={loggedInUser}
          hasAiAnnotations={hasAiAnnotations}
        ></SidebarPanelCase>
      )
    },
    {
      icon: (
        <FontAwesomeIcon
          icon={faClipboard}
          title="Annotations"
          style={{ width: "100%", height: "100%" }}
        />
      ),
      content: (
        <SidebarPanelAnnotations
          studyName={studyName}
          selectedImage={selectedImage}
          histoCase={histoCase}
          userCanDeleteAnnotations={userCanDeleteAnnotations}
          loggedInUser={loggedInUser}
        ></SidebarPanelAnnotations>
      )
    },
    {
      icon: (
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          {notificationQueryIcon ? <NotificationDot></NotificationDot> : null}
          <FontAwesomeIcon
            icon={faFlag}
            title="Queries"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      ),
      content: (
        <SidebarPanelQueries
          studyName={studyName}
          histoCase={histoCase}
          selectedImage={selectedImage}
          queryDetails={queryDetails}
          currentViewQueryObjectType={QueryObjectType.Case}
          loggedInUser={loggedInUser}
        ></SidebarPanelQueries>
      )
    }
  ];

  const iconDock = (
    <div
      style={{
        height: "100%",
        width: "50px",
        borderRight: "solid 1px #ccc",
        borderLeft: "solid 1px #ccc",
        float: "left"
      }}
    >
      {panels.map((panel, index) => (
        <PanelIcon
          key={index}
          style={{
            color: selectedPanel === index ? "#FFFFFF" : "#9A9DA0",
            backgroundColor: selectedPanel === index ? "#57B7B3" : "transparent",
            opacity: hoveredPanel === index && selectedPanel !== index ? 0.8 : 1
          }}
          onClick={() => {
            selectPanel(index);
          }}
          onMouseEnter={() => setHoveredPanel(index)}
          onMouseLeave={() => setHoveredPanel(-1)}
        >
          {panel.icon}
        </PanelIcon>
      ))}
      <PanelIcon style={{ color: "#57B7B3" }} onClick={toggleHideSidebar}>
        <FontAwesomeIcon
          icon={hideSidebar ? faArrowLeft : faArrowRight}
          style={{ position: "absolute", bottom: "15px" }}
        />
      </PanelIcon>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {iconDock}
      <InfoSidebar style={{ display: hideSidebar ? "none" : "block" }}>
        <Content isLoading={"isPending" in user || "isPending" in histoCase}>
          {panels[selectedPanel]?.content}
        </Content>
      </InfoSidebar>
      <CaseDialog />
    </div>
  );
};

export default InfoSidebarCase;
